import { computed } from 'vue';

import { searchBrandMediaTypes } from '@/app/library/constants';
import {
  baseQueryParamMapping,
  defaultReportingPeriod,
} from '@/components/core/analytics/queryParamMapping';
import { getMetrics, METRICS } from '@/models/metrics.enum';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { usePlatformStore } from '@/stores/platform';

export const useYourPosts = () => {
  const metrics = getMetrics('yourPosts');
  const platformStore = usePlatformStore();

  const isAdsConnected = !platformStore.isPlatformNeverConnected('tiktok_ads');

  const sortOptions = computed(() => {
    const options = [{ label: 'Recently Published', value: 'DATE' }];

    if (isAdsConnected) {
      // 1) Organic
      options.push({
        title: 'Organic',
        children: metrics.all([
          METRICS.TIKTOK.ORGANIC_LIKES,
          METRICS.TIKTOK.ORGANIC_REACH,
          METRICS.TIKTOK.ORGANIC_SHARES,
          METRICS.TIKTOK.ORGANIC_VIEWS,
        ]),
      });
    }

    // 2) Organic + Promoted (always visible)
    options.push({
      title: 'Organic + Promoted',
      children: metrics.all([
        METRICS.TIKTOK.AVERAGE_TIME_WATCHED,
        METRICS.TIKTOK.COMMENTS,
        METRICS.TIKTOK.FULL_VIDEO_WATCHED_RATE,
        METRICS.TIKTOK.ENGAGEMENT_RATE,
        METRICS.TIKTOK.ENTERTAINMENT_SCORE,
        METRICS.TIKTOK.IMPRESSIONS_FOLLOW_RATE,
        METRICS.TIKTOK.FYP_VIEWS,
        METRICS.TIKTOK.IMPRESSIONS_HASHTAG_RATE,
        METRICS.TIKTOK.LIKES,
        METRICS.TIKTOK.IMPRESSIONS_PERSONAL_PROFILE_RATE,
        METRICS.TIKTOK.REACH,
        METRICS.TIKTOK.RETENTION_RATE,
        METRICS.TIKTOK.IMPRESSIONS_SEARCH_RATE,
        METRICS.TIKTOK.SHARES,
        METRICS.TIKTOK.IMPRESSIONS_SOUND_RATE,
        METRICS.TIKTOK.TOTAL_ENGAGEMENTS,
        METRICS.TIKTOK.TOTAL_TIME_WATCHED,
        METRICS.TIKTOK.DURATION,
        METRICS.TIKTOK.VIDEO_VIEWS,
        METRICS.TIKTOK.VIEWS_BASED_ENGAGEMENT_RATE,
      ]),
    });

    if (isAdsConnected) {
      // 3) Promoted
      options.push({
        title: 'Promoted',
        children: metrics.all([
          METRICS.TIKTOK.PAID_LIKES,
          METRICS.TIKTOK.PAID_REACH,
          METRICS.TIKTOK.PAID_SHARES,
          METRICS.TIKTOK.PAID_VIEWS,
          METRICS.TIKTOK.PAID_ENGAGEMENTS,
          METRICS.TIKTOK.PAID_ENGAGEMENT_RATE,
          METRICS.TIKTOK.PAID_VIEWS_BASED_ENGAGEMENT_RATE,
        ]),
      });
    }

    return options;
  });

  const banners = computed(() => {
    const bannerConfigs = [];

    if (platformStore.isPlatformNeverConnected(PLATFORM_CONNECTION.TIKTOK_ADS)) {
      bannerConfigs.push({
        severity: 'info',
        message: 'Connect your TikTok Ads account for promoted post and ad insights',
        callToAction: 'Connect',
        onAction: () => platformStore.connect(PLATFORM_CONNECTION.TIKTOK_ADS),
      });
    }

    if (
      !platformStore.isPlatformNeverConnected(PLATFORM_CONNECTION.TIKTOK_ADS) &&
      !platformStore.isPlatformConnected(PLATFORM_CONNECTION.TIKTOK_ADS)
    ) {
      bannerConfigs.push({
        severity: 'warn',
        message: 'TikTok Ads not connected, data may not be current',
        callToAction: 'Reconnect',
        onAction: () => platformStore.connect(PLATFORM_CONNECTION.TIKTOK_ADS),
      });
    }

    return bannerConfigs;
  });

  return {
    title: 'TikTok Posts',
    channelFilterKey: 'tiktok',
    banners,
    filters: {
      brandMediaTypes: [searchBrandMediaTypes.TIKTOK_OWNED],
      default: {
        captionContain: [],
        captionNotContain: [],
        contentTags: [],
        contentTagMatchType: 'ANY',
        distributionType: undefined,
        reportingPeriod: defaultReportingPeriod,
        sort: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
      options: {
        distributionType: [
          { label: 'Organic Posts', value: 'ORGANIC' },
          { label: 'Promoted Posts', value: 'PROMOTED' },
        ],
      },
      queryParamMapping: {
        ...baseQueryParamMapping,
        captionContain: { queryParamName: 'captionContain', type: 'array', elementType: 'string' },
        captionNotContain: {
          queryParamName: 'captionNotContain',
          type: 'array',
          elementType: 'string',
        },
        distributionType: { queryParamName: 'distributionType', type: 'string' },
      },
      scope: 'tiktokYourPosts',
      sortDetails: {
        options: sortOptions,
        default: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
    },
    download: {
      pdf: {
        fileName: 'yourTikTokPostsPDF',
        type: 'tikTokYourPosts',
        label: 'Your TikTok Posts PDF',
        pendingKey: 'tikTokYourPostsReportPending',
      },
      csv: {
        fileName: 'yourTikTokPostsCSV',
        type: 'tikTokYourPosts',
        label: 'Your TikTok Posts CSV',
        pendingKey: 'tikTokYourPostsCSVPending',
      },
    },
    displayMetrics: metrics.all([
      METRICS.TIKTOK.ENTERTAINMENT_SCORE,
      METRICS.TIKTOK.VIDEO_VIEWS,
      METRICS.TIKTOK.TOTAL_ENGAGEMENTS,
      METRICS.TIKTOK.AVERAGE_TIME_WATCHED,
    ]),
    metricLabels: metrics.all(Object.values(METRICS.TIKTOK)),
    platformConnection: PLATFORM_CONNECTION.TIKTOK,
    shouldShowDistributionTypeFilter: computed(() => isAdsConnected),
  };
};
